import { Chip, Divider, ListItemText, MenuItem, Stack, Typography } from "@mui/material";
import classNames from "classnames";
import { type TFunction } from "next-i18next";
import { useState } from "react";
import { match } from "ts-pattern";
import { value } from "~/components/helpers";
import { DropDown } from "~/components/ui/core/DropDown";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useI18n } from "~/lib/i18n/useI18n";
import { chain } from "~/lib/lodash";
import { type GetMarketDataStatsMeasure } from "~/pages/api/get-market-data-stats";
import { Measure } from "~/services/employee";

type Props<T extends GetMarketDataStatsMeasure | Measure> = {
  inputValue: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
  showVariablePay?: boolean;
  label?: string;
  showDetailedMeasures?: boolean;
};

export const formatMeasure = (t: TFunction, measure: Measure): string => {
  return match(measure)
    .with(Measure.TotalCash, () => {
      return t("components.measure.format-measure.total-cash");
    })
    .with(Measure.BaseSalary, () => {
      return t("components.measure.format-measure.base-salary");
    })
    .with(Measure.OnTargetEarnings, () => {
      return t("components.measure.format-measure.on-target-earnings");
    })
    .with(Measure.OnTargetBonus, () => {
      return t("components.measure.format-measure.variable-pay");
    })
    .with(Measure.ProfitSharing, () => {
      return t("components.measure.format-measure.profit-sharing");
    })
    .with(Measure.Equity, () => {
      return t("components.measure.format-measure.equity");
    })
    .with(Measure.PaidBonus, () => {
      return t("components.measure.format-measure.paid-bonus");
    })
    .otherwise(() => {
      return "";
    });
};

const formatMeasureDescription = (t: TFunction, measure: Measure) => {
  return match(measure)
    .with(Measure.TotalCash, () => {
      return t("components.measure.format-measure-description.total-cash");
    })
    .with(Measure.BaseSalary, () => {
      return t("components.measure.format-measure-description.base-salary");
    })
    .with(Measure.OnTargetEarnings, () => {
      return t("components.measure.format-measure-description.on-target-earnings");
    })
    .with(Measure.OnTargetBonus, () => {
      return t("components.measure.format-measure-description.variable-pay");
    })
    .with(Measure.ProfitSharing, () => {
      return t("components.measure.format-measure-description.profit-sharing");
    })
    .with(Measure.Equity, () => {
      return t("components.measure.format-measure-description.equity");
    })
    .with(Measure.PaidBonus, () => {
      return t("components.measure.format-measure-description.paid-bonus");
    })
    .otherwise(() => {
      return "";
    });
};

export const MeasureSelector = <T extends GetMarketDataStatsMeasure | Measure>({
  inputValue,
  onChange,
  disabled,
  className,
  showVariablePay,
  label,
  showDetailedMeasures = false,
}: Props<T>) => {
  const [open, setOpen] = useState<boolean>(false);
  const { t } = useI18n();

  const { CAN_ACCESS_DETAILED_MEASURES } = useFeatureFlags();

  const onMeasureSelection = (value: T) => {
    onChange(value);
    setOpen(false);
  };

  const detailedMeasures = ["profitSharing", "equity", "paidBonus"];
  const measuresWithoutFixedBonus = Object.values(Measure).filter((measure) => measure !== Measure.FixedBonus);

  const measures = value(() => {
    return CAN_ACCESS_DETAILED_MEASURES && showDetailedMeasures
      ? measuresWithoutFixedBonus
      : chain(measuresWithoutFixedBonus)
          .filter((measure) => (showVariablePay ? true : measure !== Measure.OnTargetBonus))
          .filter((measure) => !detailedMeasures.includes(measure))
          .value();
  });

  return (
    <DropDown
      disabled={disabled}
      className={className}
      open={open}
      ButtonProps={{
        endIcon: null,
      }}
      onOpenChange={(open: boolean) => setOpen(open)}
      label={
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography alignContent="center" className="text-[13px] font-medium">
            {label ?? t("components.ui.measure-selector.market-data")}
          </Typography>

          <Divider orientation="vertical" flexItem />

          <Chip color="primary-alt" size="small" label={formatMeasure(t, inputValue)} />
        </Stack>
      }
    >
      {measures.map((measure) => (
        <MenuItem
          className={classNames({
            "bg-primary-100": inputValue === measure,
          })}
          key={measure}
          value={measure}
          onClick={() => onMeasureSelection(measure as T)}
        >
          <Stack>
            <ListItemText>{formatMeasure(t, measure)}</ListItemText>

            <Typography variant="caption" color="grey.500">
              {formatMeasureDescription(t, measure)}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </DropDown>
  );
};
